import { z } from 'zod'
import { ADLSchema, BaseEvent, CommonsEventDetail, ExtraOptions, WebDetails } from '@mmt/events-schema'
import { EventNameEnum, CLICK_EVENT_TIMEOUT, ERROR_EVENT_TYPE, PAGE_RENDERED_EVENT_NAME } from '../validation/enums'
import { createADLSchemaValidation } from './validator'
import { formatZodError } from '../validation/validator'

class ADLSchemaImpl<T extends CommonsEventDetail> {
  private adobeSchema: ADLSchema<T>

  constructor(schema: BaseEvent<T>, extraOptions?: ExtraOptions) {
    const adobeSchema = this.createAdobeSchema(schema, extraOptions)
    this.validateAdobeSchema(adobeSchema)
    this.adobeSchema = adobeSchema
    this.pushToADL()
  }

  private createAdobeSchema(schema: BaseEvent<T>, extraOptions?: ExtraOptions): ADLSchema<T> {
    let eventName = `xdm_${schema.event_detail.event_name}`
    let webData: WebDetails

    if (schema.event_detail.event_name === PAGE_RENDERED_EVENT_NAME) {
      webData = {
        webPageDetails: {
          name: schema.page_context.page_name || window.location.pathname,
          pageViews: { value: 1 },
        },
      }
      if (schema?.error_details_list?.length) webData.webPageDetails.isErrorPage = true
    } else {
      eventName = EventNameEnum.safeParse(schema.event_detail.event_name).success
        ? schema.event_detail.event_type === ERROR_EVENT_TYPE
          ? 'xdm_error'
          : `xdm_${schema.event_detail.event_name}`
        : 'xdm_cta_click'

      webData = {
        webInteraction: {
          name: schema.event_detail.event_name,
          type: 'other',
          url: extraOptions?.destinationUrl || '',
          linkClicks: { value: 1 },
        },
      }
    }

    return {
      event: eventName,
      web: webData,
      _makemytrip: schema,
    }
  }

  private validateAdobeSchema(schema: ADLSchema<T>) {
    try {
      const validator = createADLSchemaValidation(schema._makemytrip.event_detail.event_name)
      validator.parse(schema)
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formattedError = formatZodError(error)
        throw new Error(`Adobe Analytics Schema validation failed: ${JSON.stringify(formattedError, null, 2)}`)
      }
      throw error
    }
  }

  private pushToADL() {
    if (!window.adobeDataLayer) {
      window.adobeDataLayer = []
    }

    const adlData = this.adobeSchema

    try {
      if (adlData.event === 'xdm_cta_click') {
        setTimeout(() => {
          window.adobeDataLayer?.push(adlData)
        }, CLICK_EVENT_TIMEOUT)
      } else window.adobeDataLayer.push(adlData)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to push to Adobe Data Layer: ${errorMessage}`)
    }
  }
}

export { ADLSchemaImpl }
